import React, { useCallback, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Info, Lock, RefreshCw, ShieldAlert } from "lucide-react";
import useFinancialReconciliation from "../hooks/useFinancialReconciliation";
import { isTetra } from "../utils/access";
import { formatINR } from "../utils/formatINR";
import {
  MAX_RANGE_DAYS,
  RANGE_PRESETS,
  daysBetween,
  formatDay,
  istToday,
  relativeChange,
} from "../utils/finance";
import { DeltaBadge } from "../components/Analytics/KPICard";
import DailyFlowTable from "../components/Finance/DailyFlowTable";
import RecordsDrawer from "../components/Finance/RecordsDrawer";
import { GroupSettlements, RefundAnalysis } from "../components/Finance/Sections";

const updatedFormatter = new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });

function KPI({ label, value, tone = "text-ink", description, change, invertChange, previousLabel, footer }) {
  return (
    <div className="card flex flex-col p-4">
      <p className="text-[12px] font-medium uppercase tracking-wide text-neutral-500">{label}</p>
      <p className={`mt-1.5 text-[22px] font-semibold leading-tight tabular-nums ${tone}`}>{value}</p>
      <p className="mt-1 text-[12px] text-neutral-500">{description}</p>
      <div className="mt-auto pt-2">
        {change !== undefined && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-neutral-500">
            <DeltaBadge change={change} invert={invertChange} />
            {change !== null && <span>vs {previousLabel}</span>}
          </div>
        )}
        {footer}
      </div>
    </div>
  );
}

function ReconciliationStatus({ data }) {
  const t = data.totals;
  const pendingDays = data.days.filter((d) => d.status === "pending").length;
  const mismatchDays = data.days.filter((d) => d.status === "mismatch").length;
  const status = t.mismatch_count > 0 ? "mismatch" : pendingDays > 0 ? "pending" : "reconciled";
  const meta = {
    reconciled: { icon: CheckCircle2, tone: "border-green-200 bg-green-50", iconTone: "text-green-600", title: "Reconciled" },
    pending: { icon: Clock, tone: "border-amber-200 bg-amber-50", iconTone: "text-amber-600", title: "Pending reconciliation" },
    mismatch: { icon: AlertTriangle, tone: "border-red-200 bg-red-50", iconTone: "text-red-600", title: "Mismatch found" },
  }[status];
  const Icon = meta.icon;
  const credited = t.received + t.mismatch_difference;

  return (
    <section className={`rounded-xl border p-4 sm:p-5 ${meta.tone}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex gap-3">
          <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${meta.iconTone}`} />
          <div>
            <h2 className="text-[15px] font-semibold text-ink">{meta.title}</h2>
            <p className="mt-0.5 max-w-2xl text-[13px] text-neutral-700">
              {status === "mismatch" &&
                `${t.mismatch_count} record${t.mismatch_count === 1 ? "" : "s"} on ${mismatchDays} day${mismatchDays === 1 ? "" : "s"} don't match between Razorpay and the CMS. Open a day marked Mismatch below to see each one.`}
              {status === "pending" &&
                `Every payment matches, but settlements or refunds on ${pendingDays} day${pendingDays === 1 ? " are" : "s are"} still in progress.`}
              {status === "reconciled" && "Every Razorpay payment in this period matches a balance credit in the CMS, and every refund is recorded."}
            </p>
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-4 text-[13px] lg:min-w-[26rem]">
          <div>
            <dt className="text-neutral-600">Received (Razorpay)</dt>
            <dd className="font-semibold tabular-nums text-ink">{formatINR(t.received)}</dd>
          </div>
          <div>
            <dt className="text-neutral-600">Credited in CMS</dt>
            <dd className="font-semibold tabular-nums text-ink">{formatINR(credited)}</dd>
          </div>
          <div>
            <dt className="text-neutral-600">Difference</dt>
            <dd className={`font-semibold tabular-nums ${t.mismatch_difference ? "text-red-700" : "text-ink"}`}>{formatINR(t.mismatch_difference)}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

export default function FinancialReconciliation() {
  const allowed = isTetra();
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(() => RANGE_PRESETS.find((p) => p.id === "7d").range());
  const [custom, setCustom] = useState(range);
  const [customError, setCustomError] = useState("");
  const [drawer, setDrawer] = useState(null);

  const { data, isLoading, error, lastUpdated, refetch } = useFinancialReconciliation(allowed ? range.from : null, allowed ? range.to : null);

  const groupNames = useMemo(() => Object.fromEntries((data?.groups || []).map((g) => [g.group_id, g.group_name])), [data]);

  const openRecords = useCallback(
    (req) => setDrawer({ fromDate: range.from, toDate: range.to, ...req }),
    [range]
  );
  const closeDrawer = useCallback(() => setDrawer(null), []);

  if (!allowed) {
    return (
      <div className="card mx-auto mt-10 max-w-md p-8 text-center">
        <Lock className="mx-auto h-8 w-8 text-neutral-400" />
        <h1 className="mt-3 text-lg font-semibold text-ink">Not available</h1>
        <p className="mt-1 text-sm text-neutral-500">Financial reconciliation is only available to Tetra Motors administrators.</p>
      </div>
    );
  }

  const choosePreset = (p) => {
    setPreset(p.id);
    const r = p.range();
    setRange(r);
    setCustom(r);
    setCustomError("");
  };

  const applyCustom = (e) => {
    e.preventDefault();
    const today = istToday();
    if (!custom.from || !custom.to) return setCustomError("Choose both dates.");
    if (custom.from > custom.to) return setCustomError("The start date is after the end date.");
    if (custom.to > today) return setCustomError("The end date can't be in the future.");
    if (daysBetween(custom.from, custom.to) > MAX_RANGE_DAYS) return setCustomError(`Choose at most ${MAX_RANGE_DAYS} days.`);
    setCustomError("");
    setPreset("custom");
    setRange({ ...custom });
  };

  const rzpAvailable = data?.razorpay?.available;
  const t = data?.totals;
  const prev = data?.previous?.totals;
  const prevLabel = data?.previous ? `${formatDay(data.previous.from)} – ${formatDay(data.previous.to)}` : "";
  const float = data?.customer_float;
  const pendingNow = (data?.group_summary || []).reduce((s, g) => s + (g.pending_now || 0), 0);
  const pendingNowCount = (data?.group_summary || []).reduce((s, g) => s + (g.pending_now_count || 0), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="page-title">Tetra Financial Reconciliation</h1>
            <span className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600">
              <ShieldAlert className="h-3 w-3" /> Tetra only
            </span>
          </div>
          <p className="page-subtitle">
            Money received from customers, paid out to groups and refunded: {formatDay(range.from)}
            {range.from !== range.to && ` – ${formatDay(range.to)}`} (IST)
          </p>
        </div>
        <div className="flex items-center gap-3 text-[12px] text-neutral-500">
          {isLoading ? (
            <span>Loading…</span>
          ) : (
            lastUpdated && <span>Updated today at {updatedFormatter.format(lastUpdated).toLowerCase()}</span>
          )}
          <button type="button" onClick={refetch} disabled={isLoading} className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Date filters */}
      <div className="card flex flex-col gap-3 p-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => choosePreset(p)}
              aria-pressed={preset === p.id}
              className={`rounded-md border px-3 py-1.5 text-[13px] font-medium transition ${
                preset === p.id ? "border-ink bg-ink text-white" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <form onSubmit={applyCustom} className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="field-label">From</span>
            <input type="date" className="input" value={custom.from} max={istToday()} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="field-label">To</span>
            <input type="date" className="input" value={custom.to} max={istToday()} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
          </label>
          <button type="submit" className={`btn ${preset === "custom" ? "btn-primary" : "btn-secondary"}`}>Apply</button>
          {customError && <p className="w-full text-[12px] text-red-700">{customError}</p>}
        </form>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            {error}
            <button type="button" onClick={refetch} className="ml-2 font-medium underline">Try again</button>
          </div>
        </div>
      )}

      {isLoading && !data && (
        <div className="space-y-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card h-[132px] animate-pulse bg-neutral-100/60" />
            ))}
          </div>
          <div className="card h-72 animate-pulse bg-neutral-100/60" />
          <p className="text-center text-[13px] text-neutral-500">Fetching payments, fees and refunds from Razorpay. Long ranges can take a little while the first time.</p>
        </div>
      )}

      {data && !rzpAvailable && (
        <div className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-100 p-4">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-neutral-500" />
          <div className="text-[13px] text-neutral-700">
            <p className="font-semibold text-ink">Insufficient data</p>
            <p className="mt-0.5">
              Razorpay couldn't be reached, so money received, gateway fees and refunds are unknown for this period. No totals are shown
              rather than incomplete ones. Try again in a minute.
            </p>
          </div>
        </div>
      )}

      {data && rzpAvailable && t && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <KPI
              label="Money received"
              value={formatINR(t.received)}
              tone="text-green-700"
              description={`${t.payments_count} captured Razorpay payment${t.payments_count === 1 ? "" : "s"}, including GST`}
              change={relativeChange(t.received, prev?.received)}
              previousLabel={prevLabel}
            />
            <KPI
              label="Group settlements"
              value={formatINR(t.settlements)}
              tone="text-red-700"
              description={`${t.settlements_count} payout${t.settlements_count === 1 ? "" : "s"} to groups (automatic and manual)`}
              change={relativeChange(t.settlements, prev?.settlements)}
              invertChange
              previousLabel={prevLabel}
            />
            <KPI
              label="Refunds"
              value={formatINR(t.refunds)}
              tone="text-red-700"
              description={`${t.refunds_count} processed refund${t.refunds_count === 1 ? "" : "s"}`}
              change={relativeChange(t.refunds, prev?.refunds)}
              invertChange
              previousLabel={prevLabel}
              footer={t.refunds_pending > 0 && <p className="mt-1 text-[11px] text-amber-700">{formatINR(t.refunds_pending)} more still pending</p>}
            />
            <KPI
              label="Tetra net retained"
              value={formatINR(t.net_retained)}
              tone={t.net_retained < 0 ? "text-red-700" : "text-ink"}
              description={`After ${formatINR(t.gateway_fees)} Razorpay fees (incl. ${formatINR(t.gateway_fees_gst)} GST), settlements and refunds`}
              change={relativeChange(t.net_retained, prev?.net_retained)}
              previousLabel={prevLabel}
            />
            <KPI
              label="Pending settlements"
              value={formatINR(pendingNow)}
              tone={pendingNow > 0 ? "text-amber-700" : "text-ink"}
              description={`${pendingNowCount} automatic settlement${pendingNowCount === 1 ? "" : "s"} waiting to be paid, as of now. Manual settlements have no pending state.`}
            />
            <KPI
              label="Transactions"
              value={t.transactions_count.toLocaleString("en-IN")}
              description={`${t.payments_count} payments · ${t.settlements_count} settlements · ${t.refunds_count} refunds`}
              change={relativeChange(t.transactions_count, prev?.transactions_count)}
              previousLabel={prevLabel}
            />
          </div>

          <ReconciliationStatus data={data} />

          <DailyFlowTable days={data.days} groups={data.groups} totals={t} range={data.range} onOpenRecords={openRecords} />

          <div className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
            <GroupSettlements groups={data.group_summary || []} onOpenGroup={(groupId) => openRecords({ kind: "settlements", groupId })} />
            <RefundAnalysis summary={data.refund_summary} days={data.days} onOpen={() => openRecords({ kind: "refunds" })} />
          </div>

          {/* Context and method */}
          <section className="card p-4 text-[13px] text-neutral-600 sm:p-5">
            <h2 className="section-title mb-2">How these numbers are calculated</h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong className="text-ink">Tetra net retained</strong> = money received − Razorpay gateway fees − settlements paid to groups − refunds processed.
              </li>
              <li>Money received, gateway fees and refunds come from Razorpay. Amounts are the full amount the customer paid, including GST.</li>
              <li>Settlements come from the CMS: automatic transfers count on the day they were settled; manual settlements on their verification date.</li>
              <li>
                Each payment is matched to the wallet, vehicle or QR balance it credited. Anything credited twice, not credited, or a refund that the
                CMS has no record of, is reported as a mismatch.
              </li>
              {float && (
                <li>
                  Retained money includes customer balances that haven't been spent yet: currently {formatINR(float.total)} ({formatINR(float.wallets)} in
                  wallets, {formatINR(float.vehicles)} on vehicles, {formatINR(float.unclaimed_qr)} unclaimed QR payments). Groups are paid when customers
                  charge, not when they top up.
                </li>
              )}
            </ul>
          </section>
        </>
      )}

      {drawer && <RecordsDrawer request={drawer} groupNames={groupNames} onClose={closeDrawer} />}
    </div>
  );
}
